// Engine de onboarding contextual (client-side, sem roundtrips ao broker).
//
// Objetivo: apresentar dicas escalonadas para recursos novos das telas —
// beacon (pulso passivo), popover (coachmark ancorado), dialog (anúncio) e
// tour (sequência de popovers) — dirigido por um catálogo declarativo e com
// memória local de "já visto".
//
// Princípios de desempenho (ver preocupações discutidas no plano):
//  - O gatilho é POR EVENTO de ativação de tela (frame.key = screen ID). Se
//    não há dica não-vista para a tela, o engine retorna sem anexar nada:
//    zero observers, zero timers no caso comum.
//  - A âncora só é procurada quando há dica pendente. A busca é ESCOPADA ao
//    `frame.element` e AUTO-TERMINA (achou / timeout / frame desativado).
//  - O catálogo é carregado por provider LAZY (uma vez por janela, cacheado),
//    então o binbrowser não relê um monólito por página.
//
// Reuso: o coachmark estende `BaseDropDown` (posicionamento com auto-flip,
// light-dismiss e stack já testados); o dialog reusa `CommandDialogWindow`.

import { BaseDropDown, CommandDialogWindow, DropDownPosition } from "./wts.ui.overlays"
import "./wts.ui.onboarding.scss"

// ─── Tipos do catálogo ───────────────────────────────────────────────────────

export type OnboardingStyle = "beacon" | "popover" | "dialog" | "tour"

export interface OnboardingStep {
    /** Selector do alvo desta etapa (dentro do frame, ou do shell se shell=true). */
    anchor?: string
    title?: string
    body: string
    spotlight?: boolean
    /** Âncora está no chrome do navegador (shell), não na página → resolve contra document. */
    shell?: boolean
}

export interface OnboardingItem {
    /** Identificador único e estável da dica. */
    id: string
    /** Bump para re-anunciar um recurso que mudou (re-mostra a todos). */
    rev?: number
    style: OnboardingStyle
    /** Screen ID (route key) onde a dica vale. Omitido = qualquer tela. */
    screen?: string | string[]
    /**
     * Casa a tela por PRESENÇA de um seletor no DOM, em vez do id da rota. Útil
     * para tipos de tela genéricos (ex.: dataform serve N objetos com o mesmo
     * layout): `screenMatch: ".millennium-dataform"` aplica a qualquer lista.
     */
    screenMatch?: string
    /** Selector do alvo (popover/beacon). Ignorado em dialog. */
    anchor?: string
    /** Âncora está no chrome do navegador (shell), não na página → resolve contra document. */
    shell?: boolean
    spotlight?: boolean
    /** Maior prioridade aparece primeiro na fila. Default 0. */
    priority?: number
    title?: string
    body?: string
    /** Rótulo do botão primário. Default "Entendi". */
    primaryText?: string
    /** Mostrar caixa "não mostrar novamente". Default false. */
    dismissable?: boolean
    /** Versão mínima do módulo para exibir (compara com getVersion()). */
    minVersion?: string
    /** Chave de módulo do getVersion() usada para o minVersion. Default "millennium". */
    module?: string
    /** Segmentação simples por grupo de usuário. */
    audience?: { userGroup?: string[] }
    /** Etapas do tour (style: "tour"). */
    steps?: OnboardingStep[]
}

export interface OnboardingSeen {
    rev: number
    seenAt: number
    dismissed?: boolean
    count: number
}

/** Store de "já visto". Default = localStorage por usuário; plugável no futuro. */
export interface IOnboardingStore {
    get(id: string): OnboardingSeen | undefined
    set(id: string, value: OnboardingSeen): void
    all(): Record<string, OnboardingSeen>
    clear(id?: string): void
    setUser(userKey: string): void
    /** Preferência do usuário: desligar TODA a assistência. Persistido. */
    getGlobalDisabled(): boolean
    setGlobalDisabled(value: boolean): void
}

export type CatalogProvider = () => Promise<OnboardingItem[]>

// ─── Store localStorage (espelha o padrão do access-cache do browser) ────────

const STORE_VERSION = 1
const STORE_PREFIX = "@mln:onb:"

class LocalStorageStore implements IOnboardingStore {
    private userKey = ""
    private cache: Record<string, OnboardingSeen> = {}
    private globalDisabled = false

    constructor() { this.load() }

    private storageKey() { return STORE_PREFIX + this.userKey }

    private load() {
        this.cache = {}
        this.globalDisabled = false
        try {
            const raw = localStorage.getItem(this.storageKey())
            if (!raw) return
            const data = JSON.parse(raw)
            if (data && data.version === STORE_VERSION) {
                if (data.seen && typeof data.seen === "object") this.cache = data.seen
                this.globalDisabled = data.globalDisabled === true
            }
        } catch { /* store corrompido → começa vazio */ }
    }

    private flush() {
        try {
            localStorage.setItem(this.storageKey(), JSON.stringify({
                version: STORE_VERSION,
                user: this.userKey,
                seen: this.cache,
                globalDisabled: this.globalDisabled,
            }))
        } catch { /* quota/privado → ignora, degrada para memória */ }
    }

    setUser(userKey: string) {
        if (userKey === this.userKey) return
        this.userKey = userKey
        this.load()
    }

    get(id: string) { return this.cache[id] }
    all() { return this.cache }

    set(id: string, value: OnboardingSeen) {
        this.cache[id] = value
        this.flush()
    }

    clear(id?: string) {
        if (id) delete this.cache[id]
        else this.cache = {}
        this.flush()
    }

    getGlobalDisabled() { return this.globalDisabled }
    setGlobalDisabled(value: boolean) {
        this.globalDisabled = value
        this.flush()
    }
}

// ─── Utilidades ──────────────────────────────────────────────────────────────

const reduceMotion = (() => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches }
    catch { return false }
})()

/** Compara versões "a.b.c" numericamente. Retorna -1 | 0 | 1. */
function compareVersion(a: string, b: string): number {
    const pa = a.split(/[.\-]/).map(n => parseInt(n, 10) || 0)
    const pb = b.split(/[.\-]/).map(n => parseInt(n, 10) || 0)
    const len = Math.max(pa.length, pb.length)
    for (let i = 0; i < len; i++) {
        const x = pa[i] ?? 0, y = pb[i] ?? 0
        if (x !== y) return x < y ? -1 : 1
    }
    return 0
}

/** Elemento presente E de fato visível ao usuário (não display:none/oculto/zero-size). */
function isElementVisible(el: HTMLElement): boolean {
    if (!el.isConnected) return false
    const cv = (el as unknown as { checkVisibility?: () => boolean }).checkVisibility
    if (typeof cv === "function" && !cv.call(el)) return false
    const r = el.getBoundingClientRect()
    return r.width > 0 && r.height > 0
}

/**
 * Resolve a âncora quando ela estiver PRESENTE E VISÍVEL dentro do `root` — não
 * apenas no DOM (querySelector casa elemento oculto). É a peça central do modelo
 * reativo: em vez de esperar um timeout e "mostrar mesmo assim", só resolve com
 * um elemento visível; se ele nunca aparecer na janela (ou o frame desativar via
 * `signal`), resolve `null` e a dica é ADIADA (não é mostrada fora de lugar).
 *
 * Reativo e auto-terminável: MutationObserver (DOM/atributos) + poll leve para
 * mudanças de layout que não geram mutação (ex.: ancestral que passa a ser
 * exibido). Confirma a visibilidade por 2 frames antes de resolver (anti-flicker).
 * `maxWaitMs` é apenas um guard de vazamento — o gatilho real é a visibilidade.
 */
function waitForVisibleAnchor(
    root: HTMLElement,
    selector: string,
    opts: { signal?: AbortSignal, maxWaitMs?: number } = {}
): Promise<HTMLElement | null> {
    const { signal, maxWaitMs = 12000 } = opts
    const pick = (): HTMLElement | null => {
        for (const el of Array.from(root.querySelectorAll<HTMLElement>(selector)))
            if (isElementVisible(el)) return el
        return null
    }
    if (signal?.aborted) return Promise.resolve(null)

    return new Promise(resolve => {
        let done = false
        let raf = 0
        const finish = (el: HTMLElement | null) => {
            if (done) return
            done = true
            obs.disconnect()
            clearInterval(poll)
            clearTimeout(timer)
            if (raf) cancelAnimationFrame(raf)
            signal?.removeEventListener("abort", onAbort)
            resolve(el)
        }
        // Confirma que a âncora SEGUE visível por 2 frames antes de resolver:
        // evita disparar em cima de um elemento que pisca durante a montagem.
        const trySettle = () => {
            if (done || raf) return
            if (!pick()) return
            let frames = 0
            const step = () => {
                if (done) return
                const still = pick()
                if (!still) { raf = 0; return }      // sumiu → volta a observar
                if (++frames >= 2) return finish(still)
                raf = requestAnimationFrame(step)
            }
            raf = requestAnimationFrame(step)
        }
        const onAbort = () => finish(null)
        const obs = new MutationObserver(trySettle)
        obs.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "hidden"] })
        const poll = setInterval(trySettle, 300)   // layout muda sem mutação de DOM
        const timer = setTimeout(() => finish(null), maxWaitMs)
        signal?.addEventListener("abort", onAbort)
        trySettle()
    })
}

// ─── Spotlight (recorte de foco) ─────────────────────────────────────────────

class Spotlight {
    private el?: HTMLElement
    private onReflow = () => this.update()
    private target?: HTMLElement

    show(target: HTMLElement) {
        this.target = target
        const el = this.el = document.createElement("div")
        el.className = "mln-onb-spotlight"
        if (reduceMotion) el.classList.add("mln-onb-no-anim")
        document.body.appendChild(el)
        this.update()
        window.addEventListener("scroll", this.onReflow, true)
        window.addEventListener("resize", this.onReflow)
    }

    private update() {
        if (!this.el || !this.target) return
        const r = this.target.getBoundingClientRect()
        const pad = 6
        this.el.style.left = `${r.left - pad}px`
        this.el.style.top = `${r.top - pad}px`
        this.el.style.width = `${r.width + pad * 2}px`
        this.el.style.height = `${r.height + pad * 2}px`
    }

    hide() {
        window.removeEventListener("scroll", this.onReflow, true)
        window.removeEventListener("resize", this.onReflow)
        this.el?.remove()
        this.el = undefined
        this.target = undefined
    }
}

// ─── Coachmark (popover ancorado, reusa BaseDropDown) ────────────────────────

export interface PopoverContent {
    title?: string
    body: string
    primaryText?: string
    secondaryText?: string
    dismissable?: boolean
    /** Oculta o link "Não mostrar dicas" (ex.: no balão de confirmação da própria ação). */
    hideDisableAll?: boolean
    /** Progresso do tour, ex: "1 / 3". */
    progress?: string
}

export interface PopoverResult {
    /** primary = Entendi/Próximo/Concluir; secondary = Voltar; dismiss = ×/fora;
     *  disable-all = "Não mostrar dicas" (desliga TODA a assistência). */
    action: "primary" | "secondary" | "dismiss" | "disable-all"
    /** Marcado "não mostrar novamente". */
    dontShowAgain: boolean
}

class Coachmark extends BaseDropDown {
    private _resolve?: (r: PopoverResult) => void
    private _dontShow = false
    private _anchor?: HTMLElement
    private _watch?: any

    protected override createElement(): HTMLElement {
        const el = document.createElement("div")
        el.className = "mln-onb-popover" + (reduceMotion ? " mln-onb-no-anim" : "")
        el.style.position = "absolute"
        el.style.opacity = "0"
        el.style.zIndex = "100001"
        el.style.height = "auto"
        el.style.overflow = "visible"
        return el
    }

    private buildBody(el: HTMLElement, content: PopoverContent) {
        // Sem "ponta"/seta: o BaseDropDown pode virar o popover (acima/abaixo) e
        // uma seta fixa apontaria para o lado errado. O spotlight + proximidade
        // já conectam o balão ao elemento.
        const inner = document.createElement("div")
        inner.className = "mln-onb-popover-inner"

        if (content.progress) {
            const prog = document.createElement("div")
            prog.className = "mln-onb-progress"
            prog.textContent = content.progress
            inner.appendChild(prog)
        }

        if (content.title) {
            const head = document.createElement("div")
            head.className = "mln-onb-head"
            head.textContent = content.title
            inner.appendChild(head)
        }

        const body = document.createElement("div")
        body.className = "mln-onb-body"
        body.textContent = content.body
        inner.appendChild(body)

        const actions = document.createElement("div")
        actions.className = "mln-onb-actions"

        // Link discreto para o usuário desligar TODA a assistência sem precisar
        // ir ao menu do avatar. O engine mostra onde reativar.
        if (!content.hideDisableAll) {
            const disableAll = document.createElement("button")
            disableAll.className = "mln-onb-disable-all"
            disableAll.textContent = "Não mostrar dicas"
            disableAll.onclick = () => this.finish("disable-all")
            actions.appendChild(disableAll)
        }

        if (content.secondaryText) {
            const sec = document.createElement("button")
            sec.className = "mln-button mln-onb-btn-secondary"
            sec.textContent = content.secondaryText
            sec.onclick = () => this.finish("secondary")
            actions.appendChild(sec)
        }

        const prim = document.createElement("button")
        prim.className = "mln-button-primary mln-onb-btn-primary"
        prim.textContent = content.primaryText || "Entendi"
        prim.onclick = () => this.finish("primary")
        actions.appendChild(prim)

        // "×" — dispensa leve e deliberada: fecha por agora, reaparece na próxima
        // visita (até o teto anti-insistência). NÃO suprime permanentemente.
        const close = document.createElement("button")
        close.className = "mln-onb-close"
        close.setAttribute("aria-label", "Fechar")
        close.innerHTML = "&times;"
        close.onclick = () => this.finish("dismiss")
        inner.appendChild(close)

        inner.appendChild(actions)
        el.appendChild(inner)
    }

    private finish(action: PopoverResult["action"]) {
        if (this._watch) { clearInterval(this._watch); this._watch = undefined }
        const resolve = this._resolve
        this._resolve = undefined
        void this.close()
        resolve?.({ action, dontShowAgain: this._dontShow })
    }

    async show(anchor: HTMLElement, content: PopoverContent): Promise<PopoverResult> {
        this._dontShow = false
        this._anchor = anchor
        // Exige ação deliberada (Entendi/×). Clique fora e Esc NÃO dispensam —
        // evita o usuário dispensar sem querer.
        this.canLightDismiss = false
        return new Promise<PopoverResult>(resolve => {
            this._resolve = resolve
            this.onDropDown = (e) => {
                if (e.open) {
                    this.buildBody(e.element, content)
                    // Segurança: se a âncora sair do DOM (usuário navegou p/ outra
                    // tela), fecha como "dismiss" (não permanente) para não deixar
                    // popover órfão flutuando sobre outra tela.
                    this._watch = setInterval(() => {
                        if (!this._anchor || !this._anchor.isConnected) this.finish("dismiss")
                    }, 400)
                } else {
                    if (this._watch) { clearInterval(this._watch); this._watch = undefined }
                    const r = this._resolve
                    this._resolve = undefined
                    r?.({ action: "dismiss", dontShowAgain: this._dontShow })
                }
            }
            void this.open({ refElement: anchor, x: "left", y: "bottom", width: 320 } as DropDownPosition)
        })
    }
}

// ─── Beacon (pulso passivo) ──────────────────────────────────────────────────

class Beacon {
    private el?: HTMLElement
    private target?: HTMLElement
    private onReflow = () => this.update()

    show(target: HTMLElement, onClick: () => void) {
        this.target = target
        const el = this.el = document.createElement("div")
        el.className = "mln-onb-beacon" + (reduceMotion ? " mln-onb-no-anim" : "")
        el.innerHTML = `<span class="mln-onb-beacon-dot"></span><span class="mln-onb-beacon-ring"></span>`
        el.onclick = (ev) => { ev.stopPropagation(); onClick() }
        document.body.appendChild(el)
        this.update()
        window.addEventListener("scroll", this.onReflow, true)
        window.addEventListener("resize", this.onReflow)
    }

    private update() {
        if (!this.el || !this.target) return
        if (!this.target.isConnected) return this.hide()
        const r = this.target.getBoundingClientRect()
        this.el.style.left = `${r.right - 8}px`
        this.el.style.top = `${r.top - 8}px`
    }

    hide() {
        window.removeEventListener("scroll", this.onReflow, true)
        window.removeEventListener("resize", this.onReflow)
        this.el?.remove()
        this.el = undefined
        this.target = undefined
    }
}

// ─── Engine ──────────────────────────────────────────────────────────────────

const MAX_PER_SESSION = 4          // teto de dicas "bloqueantes" novas por sessão
const MAX_BEACON_IMPRESSIONS = 5   // após N aparições sem clique, para de insistir
const MAX_SOFT_DISMISS = 3         // após N impressões sem conclusão (×/navegou/reload), para de reaparecer
const TOUR_STEP_TIMEOUT = 2000     // espera curta pela âncora de um passo de tour antes de pausar

interface FrameLike {
    readonly element: HTMLElement
    key: string
    route?: { urlKey?: string, key?: string, target?: string } | undefined
    moduleName?: string
}

/**
 * Deriva os identificadores possíveis da tela a partir do frame. Uma tela pode
 * ser reconhecida por vários campos dependendo de como foi navegada (menu vs.
 * navigate() cru): key, route.urlKey/key, target (ex.: "_blank,#reports_designer")
 * e moduleName. Normaliza removendo "#" e o prefixo de localização antes da vírgula.
 */
function frameScreenKeys(frame: FrameLike): string[] {
    const norm = (raw?: string): string => {
        let s = (raw || "").trim()
        if (!s) return ""
        const comma = s.lastIndexOf(",")
        if (comma >= 0) s = s.slice(comma + 1)       // "_blank,#alvo" → "#alvo"
        if (s.charAt(0) === "#") s = s.slice(1)       // "#alvo" → "alvo"
        const amp = s.indexOf("&")
        if (amp >= 0) s = s.slice(0, amp)             // "alvo&param=..." → "alvo"
        return s.toLowerCase()
    }
    const r = frame.route
    const keys = [frame.key, r?.urlKey, r?.key, r?.target, frame.moduleName]
    return Array.from(new Set(keys.map(norm).filter(Boolean)))
}

class OnboardingEngine {
    private store: IOnboardingStore = new LocalStorageStore()
    private providers: CatalogProvider[] = []
    private catalog?: OnboardingItem[]
    private catalogPromise?: Promise<OnboardingItem[]>
    private byScreen = new Map<string, OnboardingItem[]>()
    private anyScreen: OnboardingItem[] = []
    private matchScreen: OnboardingItem[] = []
    private openCoach?: Coachmark

    private versionInfo?: Record<string, string | undefined>
    private userGroup = ""

    private presentedThisSession = 0
    private activeBeacons: Beacon[] = []
    // Ids de dicas ARMADAS (observando âncora) ou em apresentação — evita que uma
    // nova ativação de tela (ou retrigger) re-arme a MESMA dica antes de concluir.
    private pendingIds = new Set<string>()
    // Apresentador serial (um de cada vez, por prioridade). Substitui a fila que
    // bloqueava esperando âncora: no modelo reativo, cada dica só disputa o lock
    // DEPOIS que a própria âncora está visível (ver armHint) — nada bloqueia nada.
    private presenterHeld = false
    private presenterWaiters: Array<{ priority: number, resolve: () => void }> = []
    // Escopo de observação da tela atual: abortar para os observadores da tela
    // anterior quando outra ativa (ou ao desligar tudo).
    private frameAbort?: AbortController

    /** Habilita/desabilita o engine (ex.: desligar em contingência). */
    enabled = true

    /** Log de diagnóstico. Ative com localStorage['@mln:onb:debug']='1' ou Onboarding.debug=true. */
    debug = (() => { try { return localStorage.getItem("@mln:onb:debug") === "1" } catch { return false } })()
    private dbg(...args: any[]) { if (this.debug) console.log("%c[onb]", "color:#3aa0e0", ...args) }

    // ── Configuração (chamada pelo core) ──

    /** Registra um catálogo já resolvido (uso direto/imediato). */
    register(items: OnboardingItem[]) {
        this.mergeCatalog(items)
    }

    /** Registra um provider lazy (carregado uma vez, sob demanda). */
    useCatalog(provider: CatalogProvider) {
        this.providers.push(provider)
        // Invalida o catálogo já materializado para reincluir o novo provider.
        this.catalog = undefined
        this.catalogPromise = undefined
    }

    setUser(userKey: string, userGroup = "") {
        this.store.setUser(userKey)
        this.userGroup = userGroup
    }

    setStore(store: IOnboardingStore) { this.store = store }

    private mergeCatalog(items: OnboardingItem[]) {
        this.catalog = (this.catalog ?? []).concat(items)
        this.reindex()
    }

    private reindex() {
        this.byScreen.clear()
        this.anyScreen = []
        this.matchScreen = []
        for (const item of this.catalog ?? []) {
            if (item.screenMatch) { this.matchScreen.push(item); continue }
            const screens = item.screen === undefined
                ? undefined
                : Array.isArray(item.screen) ? item.screen : [item.screen]
            if (!screens) { this.anyScreen.push(item); continue }
            for (const s of screens) {
                const key = s.toLowerCase()
                const list = this.byScreen.get(key) ?? []
                list.push(item)
                this.byScreen.set(key, list)
            }
        }
    }

    private async ensureCatalog(): Promise<OnboardingItem[]> {
        if (this.catalog && !this.providers.length) return this.catalog
        if (this.catalogPromise) return this.catalogPromise

        this.catalogPromise = (async () => {
            const loaded: OnboardingItem[] = []
            for (const p of this.providers) {
                try { loaded.push(...await p()) }
                catch (e) { console.warn("[onboarding] falha carregando catálogo", e) }
            }
            // Providers já consumidos: materializados no catálogo.
            this.providers = []
            this.mergeCatalog(loaded)
            return this.catalog ?? []
        })()
        return this.catalogPromise
    }

    private async ensureVersion() {
        if (this.versionInfo) return
        try { this.versionInfo = await millennium.browser.getVersion() }
        catch { this.versionInfo = {} }
    }

    // ── Gatilho por tela ──

    /**
     * Chamado pelo core quando uma tela termina de ativar. Fire-and-forget:
     * nunca lança para o caminho de load.
     */
    onFrameActivated(frame: FrameLike) {
        if (!this.enabled || this.store.getGlobalDisabled()) return
        void this.handleFrame(frame).catch(e => console.warn("[onboarding]", e))
    }

    /** Preferência do usuário: toda a assistência ligada? (persistido) */
    isGlobalEnabled(): boolean { return !this.store.getGlobalDisabled() }
    /** Liga/desliga TODA a assistência para este usuário (persistido). */
    setGlobalEnabled(enabled: boolean) { this.store.setGlobalDisabled(!enabled) }

    /**
     * Ação "Não mostrar dicas" do próprio popover: desliga tudo e, EM VEZ DE UM
     * TOAST, mostra um coachmark apontando o avatar — é ali que o usuário religa.
     * O balão de confirmação não traz o próprio link (hideDisableAll).
     */
    private async disableAllFromPopover() {
        this.setGlobalEnabled(false)
        this.frameAbort?.abort()   // para os observadores de âncora em andamento
        // Waiters já bloqueados no lock acordam em ordem e desistem em canPresent
        // (getGlobalDisabled=true) — não precisa cancelá-los à força.
        const avatar = document.querySelector<HTMLElement>("#usr-img")
        // Avatar visível (modo browser) → aponta para ele. Senão (embedded, sem
        // header) → cai num toast informativo.
        if (avatar && avatar.offsetParent !== null) {
            const spot = new Spotlight()
            spot.show(avatar)
            avatar.classList.add("mln-onb-target")
            const coach = new Coachmark()
            this.openCoach = coach
            try {
                await coach.show(avatar, {
                    title: "Dicas desativadas",
                    body: "Você pode reativá-las quando quiser aqui, no menu do seu usuário.",
                    primaryText: "Ok",
                    hideDisableAll: true,
                })
            } finally {
                if (this.openCoach === coach) this.openCoach = undefined
                avatar.classList.remove("mln-onb-target")
                spot.hide()
            }
        } else {
            try {
                millennium.browser.notify(
                    "Dicas de ajuda desativadas. Reative no menu do seu usuário.", "success")
            } catch { /* fora do app (ex.: teste) */ }
        }
    }

    private async handleFrame(frame: FrameLike) {
        // Navegou para outra tela: fecha um coachmark que tenha ficado aberto na
        // anterior (dispensa leve, não permanente — reaparece se voltar).
        if (this.openCoach) { const c = this.openCoach; this.openCoach = undefined; void c.close() }

        const screenKeys = frameScreenKeys(frame)
        this.dbg("frame ativado — screenKeys:", screenKeys)
        if (!screenKeys.length) return

        // O catálogo pode ainda não ter carregado (provider lazy). Como não dá
        // para saber se há dica sem o catálogo, na PRIMEIRA ativação pagamos um
        // único carregamento (cacheado). Depois disso é só filtro em memória.
        if (this.providers.length) {
            await this.ensureCatalog()
        }
        this.dbg("catálogo:", (this.catalog ?? []).length, "itens; índice telas:", [...this.byScreen.keys()])

        // Filtro barato: une descritores indexados por qualquer id da tela + os
        // globais. Nada casou → sai sem custo (sem observers, sem timers).
        const seen = new Set<OnboardingItem>()
        const candidates: OnboardingItem[] = []
        for (const k of screenKeys)
            for (const item of this.byScreen.get(k) ?? [])
                if (!seen.has(item)) { seen.add(item); candidates.push(item) }
        for (const item of this.anyScreen)
            if (!seen.has(item)) { seen.add(item); candidates.push(item) }
        // Descritores por seletor (telas genéricas tipo dataform): casam por
        // PRESENÇA do seletor no DOM da tela — um querySelector barato cada.
        for (const item of this.matchScreen)
            if (!seen.has(item) && item.screenMatch && frame.element.querySelector(item.screenMatch)) {
                seen.add(item); candidates.push(item)
            }
        if (!candidates.length) return

        await this.ensureVersion()

        this.dbg("candidatos p/ esta tela:", candidates.map(c => c.id))
        const pending = candidates
            // Dedup: já armada ou em apresentação → não re-arma (evita a mesma dica
            // aparecer duas vezes quando a tela re-ativa antes da dispensa).
            .filter(item => !this.pendingIds.has(item.id))
            .filter(item => { const ok = this.isEligible(item); if (!ok) this.dbg("  descartado (visto/gate):", item.id); return ok })
        this.dbg("pendentes:", pending.map(p => p.id))
        if (!pending.length) return

        // Novo escopo de observação para esta ativação: aborta os observadores de
        // âncora de telas anteriores (dicas que ficaram esperando âncora que não veio).
        this.frameAbort?.abort()
        this.frameAbort = new AbortController()

        // Modelo REATIVO: cada dica é "armada" independentemente — observa a própria
        // âncora e só disputa o apresentador quando ela está visível. Uma dica cuja
        // âncora nunca aparece NÃO bloqueia as outras e NÃO é mostrada fora de lugar.
        for (const item of pending)
            void this.armHint(item, frame)
    }

    private isEligible(item: OnboardingItem): boolean {
        const seen = this.store.get(item.id)
        const rev = item.rev ?? 0
        if (seen && seen.rev >= rev) {
            // Só suprime de vez quando dispensada deliberadamente ("Entendi").
            if (seen.dismissed) return false
            // Teto anti-insistência para dispensas LEVES (× ou navegou fora):
            if (item.style === "beacon" && seen.count >= MAX_BEACON_IMPRESSIONS) return false
            if (item.style !== "beacon" && seen.count >= MAX_SOFT_DISMISS) return false
        }

        // Gate de versão (defensivo: sem info de versão → não bloqueia).
        if (item.minVersion) {
            const v = this.versionInfo?.[item.module || "millennium"]
            if (v && compareVersion(v, item.minVersion) < 0) return false
        }
        // Gate de audiência.
        if (item.audience?.userGroup?.length) {
            if (!this.userGroup || !item.audience.userGroup.some(g => g.toLowerCase() === this.userGroup.toLowerCase()))
                return false
        }
        return true
    }

    // ── Apresentador serial (um de cada vez, por prioridade) ──

    /** Adquire o apresentador. Se ocupado, entra numa lista ordenada por prioridade. */
    private acquirePresenter(priority: number): Promise<void> {
        if (!this.presenterHeld) { this.presenterHeld = true; return Promise.resolve() }
        return new Promise<void>(resolve => {
            const w = { priority, resolve }
            const idx = this.presenterWaiters.findIndex(x => x.priority < priority)
            if (idx === -1) this.presenterWaiters.push(w); else this.presenterWaiters.splice(idx, 0, w)
        })
    }

    /** Libera o apresentador, passando-o ao waiter de maior prioridade (se houver). */
    private releasePresenter(): void {
        const next = this.presenterWaiters.shift()
        if (next) next.resolve()        // repassa o lock — presenterHeld continua true
        else this.presenterHeld = false
    }

    private async withPresenter(priority: number, fn: () => Promise<void>) {
        await this.acquirePresenter(priority)
        try { await fn() }
        catch (e) { console.warn("[onboarding]", e) }
        finally { this.releasePresenter() }
    }

    /** Re-checa, no momento de apresentar, se a dica ainda deve/pode aparecer. */
    private canPresent(item: OnboardingItem): boolean {
        if (this.store.getGlobalDisabled()) return false
        if (!this.isEligible(item)) return false
        if (this.presentedThisSession >= MAX_PER_SESSION) return false
        return true
    }

    /**
     * "Arma" uma dica no modelo reativo: observa a PRÓPRIA âncora e só disputa o
     * apresentador quando ela está visível. Se a âncora nunca aparece, a dica é
     * simplesmente ADIADA (não bloqueia outras, não é mostrada fora de lugar).
     */
    private async armHint(item: OnboardingItem, frame: FrameLike) {
        if (this.pendingIds.has(item.id)) return
        this.pendingIds.add(item.id)
        const signal = this.frameAbort?.signal
        try {
            if (item.style === "beacon") {
                if (!item.anchor) return
                const root = item.shell ? document.body : frame.element
                const anchor = await waitForVisibleAnchor(root, item.anchor, { signal })
                if (!anchor || !this.canPresent(item)) return
                this.showBeaconAt(item, anchor)
                return
            }
            if (item.style === "dialog") {
                await this.withPresenter(item.priority ?? 0, async () => {
                    if (this.canPresent(item)) await this.presentDialog(item)
                })
                return
            }
            // popover ou tour: espera a PRIMEIRA âncora ficar visível ANTES de
            // disputar o lock (gate de "tela pronta" específico da dica).
            const first = item.style === "tour" ? item.steps?.[0] : item
            if (item.style === "popover" && !item.anchor) return
            if (first?.anchor) {
                const firstRoot = (first.shell ?? item.shell) ? document.body : frame.element
                const anchor = await waitForVisibleAnchor(firstRoot, first.anchor, { signal })
                if (!anchor) return          // nunca ficou visível → adia p/ próxima visita
            }
            await this.withPresenter(item.priority ?? 0, async () => {
                if (!this.canPresent(item)) return
                if (item.style === "tour") await this.presentTour(item, frame)
                else await this.presentPopover(item, frame)
            })
        } catch (e) {
            console.warn("[onboarding]", e)
        } finally {
            this.pendingIds.delete(item.id)
        }
    }

    // ── Apresentação ──

    /**
     * Grava uma IMPRESSÃO: a dica foi de fato EXIBIDA. Incrementa `count` e
     * persiste ANTES da interação do usuário — assim um reload no meio da dica
     * não a faz repetir indefinidamente (o `count` acumula até o teto). Espelha
     * o que o beacon já fazia. Não mexe em `dismissed`.
     */
    private recordImpression(item: OnboardingItem) {
        const prev = this.store.get(item.id)
        this.store.set(item.id, {
            rev: item.rev ?? 0,
            seenAt: Date.now(),
            dismissed: prev?.dismissed ?? false,
            count: (prev?.count ?? 0) + 1,
        })
    }

    /**
     * Atualiza só o estado de DISPENSA após a interação. NÃO conta impressão de
     * novo (isso já foi feito no show por recordImpression) — senão cada dica
     * contaria em dobro para o teto.
     */
    private markDismissed(item: OnboardingItem, dismissed: boolean) {
        const prev = this.store.get(item.id)
        this.store.set(item.id, {
            rev: item.rev ?? 0,
            seenAt: Date.now(),
            dismissed,
            count: prev?.count ?? 1,
        })
    }

    private async presentPopover(item: OnboardingItem, frame: FrameLike) {
        // Chamado já com o lock. A âncora foi validada visível no armHint; re-resolve
        // com janela curta caso tenha mudado entre "ficou visível" e "peguei o lock".
        const root = item.shell ? document.body : frame.element
        const anchor = item.anchor
            ? await waitForVisibleAnchor(root, item.anchor, { signal: this.frameAbort?.signal, maxWaitMs: 1500 })
            : null
        if (!anchor) return   // sumiu nesse meio-tempo → adia p/ próxima visita
        try { anchor.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" }) } catch { }

        // Impressão contabilizada AGORA (a caixa vai aparecer), antes de bloquear
        // no coach.show — reload no meio não faz repetir infinito.
        this.recordImpression(item)
        this.presentedThisSession++
        await this.showPopoverFor(item, anchor)
    }

    private async showPopoverFor(item: OnboardingItem, anchor: HTMLElement, progress?: string) {
        const spot = item.spotlight ? new Spotlight() : undefined
        spot?.show(anchor)
        anchor.classList.add("mln-onb-target")
        const coach = new Coachmark()
        this.openCoach = coach
        try {
            const result = await coach.show(anchor, {
                title: item.title,
                body: item.body ?? "",
                primaryText: item.primaryText,
                dismissable: item.dismissable,
                progress,
            })
            if (result.action === "disable-all") {
                void this.disableAllFromPopover()
                this.markDismissed(item, true)
                return result
            }
            // "primary" (Entendi) = dispensa permanente; "dismiss" (×/navegou) = leve.
            this.markDismissed(item, result.action === "primary")
            return result
        } finally {
            if (this.openCoach === coach) this.openCoach = undefined
            anchor.classList.remove("mln-onb-target")
            spot?.hide()
        }
    }

    private async presentDialog(item: OnboardingItem) {
        const content = document.createElement("div")
        content.className = "mln-onb-dialog-content"
        content.innerHTML = `<div class="mln-onb-dialog-body"></div>`
        const bodyEl = content.querySelector<HTMLElement>(".mln-onb-dialog-body")!
        bodyEl.textContent = item.body ?? ""

        this.recordImpression(item)
        this.presentedThisSession++
        const dlg = new CommandDialogWindow()
        await dlg.show(content, {
            title: item.title || "Novidade",
            className: "mln-onb-dialog",
            width: "440px",
            lightDismiss: true,
            commands: [
                { id: "ok", caption: item.primaryText || "Entendi", primary: true },
            ],
        })
        this.markDismissed(item, true)
    }

    private async presentTour(item: OnboardingItem, frame: FrameLike) {
        const steps = item.steps ?? []
        if (!steps.length) return
        const signal = this.frameAbort?.signal
        // Passo é "exibível agora" se não tem âncora ou a âncora está visível AGORA
        // (checagem instantânea, sem espera) — usado só para contar progresso/rótulo.
        const showableNow = (s: OnboardingStep) => {
            if (!s.anchor) return true
            const r = (s.shell ?? item.shell) ? document.body : frame.element
            const e = r.querySelector<HTMLElement>(s.anchor)
            return !!(e && isElementVisible(e))
        }
        let shownIndex = 0
        let impressionRecorded = false
        let abandoned = false
        for (let i = 0; i < steps.length; i++) {
            const step = steps[i]
            const stepRoot = (step.shell ?? item.shell) ? document.body : frame.element
            const anchor = step.anchor
                ? await waitForVisibleAnchor(stepRoot, step.anchor, { signal, maxWaitMs: TOUR_STEP_TIMEOUT })
                : null
            if (step.anchor && !anchor) {
                // Âncora não fica visível NESTA tela (ausente ou muito lenta). PULA o
                // passo — o gate garante que nada é mostrado fora de lugar — e segue,
                // para o tour CONCLUIR em vez de pausar e repetir do passo 1 (regressão
                // do modelo reativo). O 1º passo já foi garantido visível no armHint.
                this.dbg("tour", item.id, "etapa", i, "âncora não visível, pulando:", step.anchor)
                continue
            }
            if (!anchor) continue   // passo sem âncora declarada → nada a ancorar, pula
            try { anchor.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" }) } catch { }

            // Impressão contabilizada no PRIMEIRO passo que realmente aparece.
            if (!impressionRecorded) {
                this.recordImpression(item)
                this.presentedThisSession++
                impressionRecorded = true
            }

            // Progresso e rótulo pelos passos EFETIVAMENTE exibíveis (âncora visível
            // agora), não pelo total bruto — senão o botão diria "Próximo" no último
            // passo visível e o contador pularia números ao pular passos ocultos.
            shownIndex++
            let laterCount = 0
            for (let j = i + 1; j < steps.length; j++) if (showableNow(steps[j])) laterCount++
            const last = laterCount === 0

            const spot = step.spotlight ? new Spotlight() : undefined
            spot?.show(anchor)
            anchor.classList.add("mln-onb-target")
            const coach = new Coachmark()
            this.openCoach = coach
            try {
                const result = await coach.show(anchor, {
                    title: step.title,
                    body: step.body,
                    primaryText: last ? (item.primaryText || "Concluir") : "Próximo",
                    secondaryText: shownIndex > 1 ? "Voltar" : undefined,
                    progress: `${shownIndex} / ${shownIndex + laterCount}`,
                })
                if (result.action === "disable-all") { void this.disableAllFromPopover(); this.markDismissed(item, true); return }
                if (result.action === "secondary") { i -= 2; shownIndex = Math.max(0, shownIndex - 2); continue } // volta
                if (result.action === "dismiss") { abandoned = true; break } // abandonou/fechou o tour
                // "primary"/avança → segue o loop
            } finally {
                if (this.openCoach === coach) this.openCoach = undefined
                anchor.classList.remove("mln-onb-target")
                spot?.hide()
            }
        }
        // Nada chegou a aparecer (nem o 1º passo) → não marca: retenta numa próxima
        // visita, sem consumir o teto de impressões.
        if (!impressionRecorded) return
        // Chegou ao fim (mesmo pulando passos ocultos) = CONCLUÍDO (permanente, não
        // repete). Só "leve" (reaparece com teto) se o usuário abandonou (× / navegou).
        this.markDismissed(item, !abandoned)
    }

    /** Mostra o pulso do beacon numa âncora JÁ resolvida (visível) pelo armHint. */
    private showBeaconAt(item: OnboardingItem, anchor: HTMLElement) {
        const beacon = new Beacon()
        this.activeBeacons.push(beacon)
        // Conta a impressão do pulso (para o cap de insistência), sem marcar como visto.
        this.recordImpression(item)
        beacon.show(anchor, async () => {
            beacon.hide()
            // Clique no pulso → popover, disputando o apresentador serial.
            await this.withPresenter(item.priority ?? 0, async () => {
                this.presentedThisSession++
                await this.showPopoverFor(item, anchor)
            })
        })
    }

    // ── API pública ──

    /** Dispara uma dica específica de forma imperativa (âncora precisa). */
    async request(id: string, opts?: { anchor?: HTMLElement, frame?: FrameLike, force?: boolean }) {
        await this.ensureCatalog()
        await this.ensureVersion()
        const item = (this.catalog ?? []).find(i => i.id === id)
        if (!item) return
        if (!opts?.force && !this.isEligible(item)) return

        if (opts?.anchor) {
            await this.showPopoverFor(item, opts.anchor)
            return
        }
        if (opts?.frame) {
            // Arma no modelo reativo (espera a âncora ficar visível). Garante um escopo
            // de abort válido para os observadores.
            if (!this.frameAbort || this.frameAbort.signal.aborted) this.frameAbort = new AbortController()
            void this.armHint(item, opts.frame)
        }
    }

    markSeenById(id: string, dismissed = true) {
        const item = (this.catalog ?? []).find(i => i.id === id)
        this.store.set(id, { rev: item?.rev ?? 0, seenAt: Date.now(), dismissed, count: 1 })
    }

    /** Reativa a assistência: limpa o "já visto" (de uma dica ou de todas) e,
     *  ao resetar tudo, religa a preferência global. */
    reset(id?: string) {
        this.store.clear(id)
        if (!id) this.store.setGlobalDisabled(false)
        this.presentedThisSession = 0
    }

    /** Reprocessa a tela atual (usado após "rever dicas" para mostrar já). */
    retrigger(frame: FrameLike) { this.onFrameActivated(frame) }
}

// Singleton compartilhado por TODOS os bundles (core, omni, vitrine…), no
// padrão do DROP_DOWN_STACK do overlays — garante uma fila/estado únicos.
const GLOBAL_KEY = "___MLN_ONBOARDING"
if (!(window as any)[GLOBAL_KEY])
    (window as any)[GLOBAL_KEY] = new OnboardingEngine()

export const Onboarding: OnboardingEngine = (window as any)[GLOBAL_KEY]
export type { OnboardingEngine }
